import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { approvalsApi } from "@/api/approvals";
import { MarkdownRenderer } from "@/components/chat/markdown-renderer";
import { ShieldCheck, Plus, Check, X } from "lucide-react";

const statusVariant: Record<string, "success" | "danger" | "warning" | "default"> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
  expired: "default",
};

export function Approvals() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const qk = ["approvals", tenantId];
  const { data: approvals = [], isLoading } = useQuery({
    queryKey: qk,
    queryFn: () => approvalsApi.list(tenantId),
  });
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "" });

  const createMutation = useMutation({
    mutationFn: () => approvalsApi.create({ companyId: tenantId, title: form.title, description: form.description || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk });
      setShowCreate(false);
      setForm({ title: "", description: "" });
    },
  });

  const decideMutation = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: "approved" | "rejected" }) => approvalsApi.decide(id, decision),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: qk }),
  });

  const pending = approvals.filter((a) => a.status === "pending");
  const decided = approvals.filter((a) => a.status !== "pending");

  return (
    <CenterPanel title="Approvals">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {pending.length} pending · {decided.length} decided
        </p>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
          <Plus size={16} className="mr-1.5" />
          New Request
        </Button>
      </div>

      {showCreate && (
        <Card className="mb-4">
          <CardContent className="space-y-3 pt-4">
            <Input placeholder="Approval title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <Input placeholder="Description (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <div className="flex gap-2">
              <Button size="sm" disabled={!form.title.trim() || createMutation.isPending} onClick={() => createMutation.mutate()}>Submit</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading approvals...</p>
      ) : approvals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <ShieldCheck size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No approval requests.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {approvals.map((approval) => (
            <Card key={approval.id}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary">{approval.title}</p>
                  <p className="text-xs text-text-muted">
                    {new Date(approval.createdAt).toLocaleDateString()}
                  </p>
                  {approval.description && (
                    <div className="text-xs text-text-secondary mt-1 max-h-40 overflow-auto">
                      <MarkdownRenderer content={approval.description} />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={statusVariant[approval.status] ?? "default"} className="capitalize">
                    {approval.status}
                  </Badge>
                  {approval.status === "pending" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => decideMutation.mutate({ id: approval.id, decision: "approved" })}>
                        <Check size={14} className="mr-1" /> Approve
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => decideMutation.mutate({ id: approval.id, decision: "rejected" })}>
                        <X size={14} className="mr-1" /> Reject
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </CenterPanel>
  );
}
